import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { BarList, DonutStat, PROGRESS_COLORS, StackedBar, TAG_COLORS } from '../../components/charts/index.jsx';
import { Meter, Panel, Spinner, Stat, relativeTime } from '../../components/common/index.jsx';
import { admin } from '../../lib/api.js';
import { TAG_META } from '../../lib/spans.js';

export function DashboardPage() {
  const [overview, setOverview] = useState(null);
  const [annotators, setAnnotators] = useState([]);
  const [tags, setTags] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [includeOverlap, setIncludeOverlap] = useState(false);

  useEffect(() => {
    admin.overview({ include_overlap: includeOverlap }).then(setOverview).catch(() => {});
    admin.tags({ include_overlap: includeOverlap }).then(setTags).catch(() => {});
  }, [includeOverlap]);

  useEffect(() => {
    admin.annotators().then((r) => setAnnotators(r.items)).catch(() => {});
    admin.datasets().then((r) => setDatasets(r.items)).catch(() => {});
  }, []);

  if (!overview) return <Spinner label="Loading statistics…" />;

  const c = overview.combined;
  const coco = overview.by_family.coco;
  const web = overview.by_family.web;

  const notStarted = Math.max(0, c.samples_total - c.samples_annotated);
  const inProgress = Math.max(0, c.samples_annotated - c.samples_submitted);

  return (
    <div className="page wide">
      <div className="page-head row-between">
        <div>
          <h1>Progress</h1>
          <p>Across every dataset and annotator.</p>
        </div>
        <label className="row gap-1 small muted" style={{ cursor: 'pointer' }} title={overview.counting_rules.tag_counts}>
          <input
            type="checkbox"
            checked={includeOverlap}
            onChange={(e) => setIncludeOverlap(e.target.checked)}
          />
          Count every annotation (double-annotated samples twice)
        </label>
      </div>

      <div className="stat-grid section">
        <Stat label="Images" value={c.images_total} sub={`${c.samples_total} captions`} />
        <Stat label="Assigned" value={c.images_assigned} sub={`${c.images_total - c.images_assigned} unassigned`} />
        <Stat label="Completed" value={c.images_completed} sub={`${c.percent_complete}% of the corpus`} />
        <Stat label="Remaining" value={c.images_remaining} sub="images" />
        <Stat
          label="Human tags"
          value={c.tags_total}
          sub={`${c.added_span_count} added spans (${c.added_char_count} chars)`}
        />
      </div>

      <div className="section">
        <Panel title="Caption progress">
          <StackedBar
            segments={[
              { label: 'Submitted', value: c.samples_submitted, color: PROGRESS_COLORS.done },
              { label: 'In progress', value: inProgress, color: PROGRESS_COLORS.in_progress },
              { label: 'Not started', value: notStarted, color: PROGRESS_COLORS.unassigned, fg: 'var(--fg-muted)' },
            ]}
            total={c.samples_total}
          />
        </Panel>
      </div>

      <div className="grid-2 section">
        <Panel title="COCO vs web">
          <div className="stack gap-5">
            <DonutStat
              label="COCO images"
              value={coco.images_completed}
              total={coco.images_total}
              color="var(--orange-700)"
            />
            <DonutStat
              label="Web images"
              value={web.images_completed}
              total={web.images_total}
              color="var(--blue-600)"
            />
          </div>
        </Panel>

        <Panel
          title="Tags by type"
          actions={<span className="tiny faint">{includeOverlap ? 'all annotations' : 'one per caption'}</span>}
        >
          <div className="stack gap-5">
            <div>
              <div className="section-title">Combined</div>
              <BarList
                items={[
                  { label: TAG_META.hallucinated.label, value: c.hallucinated, color: TAG_COLORS.hallucinated },
                  { label: TAG_META.uncertain.label, value: c.uncertain, color: TAG_COLORS.uncertain },
                  { label: TAG_META.wrong_uncertainty.label, value: c.wrong_uncertainty, color: TAG_COLORS.wrong_uncertainty },
                  { label: 'Added by hand', value: c.added_span_count, color: 'var(--tag-added-line)' },
                ]}
              />
            </div>
            <div className="grid-2">
              <div>
                <div className="section-title">COCO</div>
                <BarList
                  items={tagItems(coco)}
                  max={Math.max(1, ...tagItems(coco).map((i) => i.value), ...tagItems(web).map((i) => i.value))}
                />
              </div>
              <div>
                <div className="section-title">Web</div>
                <BarList
                  items={tagItems(web)}
                  max={Math.max(1, ...tagItems(coco).map((i) => i.value), ...tagItems(web).map((i) => i.value))}
                />
              </div>
            </div>
          </div>
        </Panel>
      </div>

      {tags && (
        <div className="grid-2 section">
          <Panel title="What annotators did with each tag">
            <BarList
              items={Object.entries(tags.by_operation || {}).map(([op, n]) => ({
                label: op.replace(/_/g, ' '),
                value: n,
              }))}
            />
          </Panel>
          <Panel
            title="Pipeline labels for comparison"
            actions={<span className="tiny faint">not merged with human tags</span>}
          >
            <BarList
              items={Object.entries(tags.pipeline_label_counts || {}).map(([label, n]) => ({
                label,
                value: n,
                color: label === 'unverifiable' ? 'var(--fg-faint)' : 'var(--orange-400)',
              }))}
            />
            <p className="tiny faint" style={{ marginTop: 'var(--space-3)' }}>
              {tags.note}
            </p>
          </Panel>
        </div>
      )}

      <div className="section">
        <Panel title="Annotators" bodyClass="tight">
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Annotator</th>
                  <th className="num">Assigned</th>
                  <th className="num">Done</th>
                  <th className="num">Left</th>
                  <th style={{ minWidth: 140 }}>Progress</th>
                  <th className="num">H</th>
                  <th className="num">U</th>
                  <th className="num">W</th>
                  <th className="num">Added</th>
                  <th>Last active</th>
                </tr>
              </thead>
              <tbody>
                {annotators.map((row) => (
                  <tr key={row.user.id}>
                    <td>
                      <strong>{row.user.display_name}</strong>
                      <div className="tiny faint">
                        {row.user.username} · {row.user.role}
                        {!row.user.is_active && ' · inactive'}
                      </div>
                    </td>
                    <td className="num">{row.assigned_images}</td>
                    <td className="num">{row.completed_images}</td>
                    <td className="num">{row.remaining_images}</td>
                    <td>
                      <Meter value={row.completed_images} total={row.assigned_images} className="sm" />
                    </td>
                    <td className="num">{row.tags.hallucinated}</td>
                    <td className="num">{row.tags.uncertain}</td>
                    <td className="num">{row.tags.wrong_uncertainty}</td>
                    <td className="num">{row.added_span_count}</td>
                    <td className="tiny faint nowrap">{relativeTime(row.last_activity_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <div className="section">
        <Panel
          title="Datasets"
          actions={<Link className="btn sm" to="/admin/assignments">Assign work</Link>}
          bodyClass="tight"
        >
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Dataset</th>
                  <th>Family</th>
                  <th className="num">Images</th>
                  <th className="num">Captions</th>
                  <th className="num">Annotated</th>
                  <th className="num">Submitted</th>
                  <th className="num">Missing files</th>
                  <th>Imported</th>
                </tr>
              </thead>
              <tbody>
                {datasets.map((d) => (
                  <tr key={d.key}>
                    <td>
                      <strong>{d.title}</strong>
                      <div className="tiny faint">{d.key}{!d.enabled && ' · disabled'}</div>
                    </td>
                    <td><span className="chip">{d.family}</span></td>
                    <td className="num">{d.image_count}</td>
                    <td className="num">{d.sample_count}</td>
                    <td className="num">{d.annotated_samples}</td>
                    <td className="num">{d.submitted_samples}</td>
                    <td className="num">
                      {d.missing_images > 0 ? (
                        <span className="chip danger">{d.missing_images}</span>
                      ) : (
                        0
                      )}
                    </td>
                    <td className="tiny faint nowrap">{relativeTime(d.last_ingest_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function tagItems(bucket) {
  return [
    { label: 'Hallucination', value: bucket.hallucinated, color: TAG_COLORS.hallucinated },
    { label: 'Uncertain', value: bucket.uncertain, color: TAG_COLORS.uncertain },
    { label: 'Wrong uncertainty', value: bucket.wrong_uncertainty, color: TAG_COLORS.wrong_uncertainty },
    { label: 'Added by hand', value: bucket.added_span_count, color: 'var(--tag-added-line)' },
  ];
}
